import {
  equals,
  map,
  pipe,
  pluck,
  propEq,
  startsWith,
  values,
  sort,
  reject,
  uniq,
  replace,
  subtract,
  trim,
} from 'ramda';
import { contained } from 'ramda-adjunct';
import { ItemType } from '../../constants/ItemType';
import { MainSource, Source } from '../../constants/Source';
import { SystemBoardId } from '../../constants/SystemBoardId';
import { SystemFolderId } from '../../constants/SystemFolderId';
import { getId } from '../common';
import { MediaManagerSDKAnyItem, MediaManagerSDKBoard } from './index';
import { MediaManagerSDKBoardSitesVisibility } from '../../constants/BoardSitesVisibility';

type TBoardIdChecker = (boardId: string) => boolean;
type TBoardChecker = (board: MediaManagerSDKBoard) => boolean;
type TBoardCurrentSiteOnly = (board: MediaManagerSDKBoard) => boolean;

export const isBoard = (item: MediaManagerSDKAnyItem | string): item is MediaManagerSDKBoard =>
  item && (item as MediaManagerSDKAnyItem).kind === ItemType.Board;

export const isRootBoardId: TBoardIdChecker = equals(SystemFolderId.Boards);

export const isBoardId = startsWith('board.');

export const isSystemBoardId: TBoardIdChecker = contained(values(SystemBoardId));

export const isSystemBoard: TBoardChecker = pipe(getId, isSystemBoardId);

export const isFavoritesBoard: TBoardChecker = propEq('id', SystemBoardId.Favorites);

export const isSiteFilesFolderInBoards = (source: Source, folderId: string): boolean =>
  source === MainSource.Boards && folderId && !isRootBoardId(folderId) && !isBoardId(folderId);

export const isCurrentSiteOnlyBoard: TBoardCurrentSiteOnly = propEq(
  'sitesVisibility',
  MediaManagerSDKBoardSitesVisibility.Current,
);

export const getUniqueDefaultBoardNameIndex = (boards: MediaManagerSDKBoard[], namePrefix: string): number => {
  const existedIndexes = pipe<MediaManagerSDKBoard[], string[], number[], number[], number[], number[]>(
    pluck('name'),
    map(pipe(replace(new RegExp(`${namePrefix}`), ''), trim, Number)),
    reject(isNaN),
    sort(subtract),
    uniq,
  )(boards);

  const indexesLength = existedIndexes.length;

  for (let i = 0, nextIndex = 1; i < indexesLength; ) {
    if (nextIndex !== existedIndexes[i]) {
      return nextIndex;
    }

    nextIndex++;
    i++;
  }

  return indexesLength + 1;
};

export const isUserOwnerOfBoard = (board) => propEq('userId', window.userId)(board);
